// Copyright (c) Docugami, Inc. All rights reserved.

import { CanvasHelperStatics } from "components/doc_hosting/doc_hosts/web/CanvasHelperStatics";
import { Point } from "sdv/Types";
import { IDrawCommand, IRenderer, IRendererInput, Queries } from "sdv/engine/Engine";
import { Box, boxCenterX } from "store/shared_types";
import { FeatureFlags } from "store/featureFlags/types";
import { ComponentId } from "sdv/engine/Component";
import { CursorComponent } from "sdv/components/CursorComponent";

/**
 * Renders token bounding boxes to the canvas.
 */
export class DebugRenderer implements IRenderer {
    get renderEnabled(): boolean { return true; }

    queries: Queries = {
        cursor: { types: [ComponentId.Cursor] },
    }

    render(engine: IRendererInput): IDrawCommand[] {
        const commands: IDrawCommand[] = [];
        const featureFlags = engine.resources.get("featureFlags").value;

        if (!!featureFlags[FeatureFlags.DebugTokensEnabled]) {
            const tokenLookup = engine.resources.get("pageTokens").value;
            const pages = engine.resources.get("visiblePages").value;

            const cursorComp = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!;
            const boxes: Box[] = [];
            for (const page of pages) {
                for (const pageRange of page.tokenRanges) {
                    for (let tok = pageRange.fromTok; tok <= pageRange.toTok; ++tok) {
                        const box = tokenLookup[tok];
                        if (!!box && tok !== cursorComp.token) {
                            boxes.push(box);
                        }
                    }
                }
            }

            if (!!boxes.length) {
                commands.push(context => drawBoxes(context, boxes));
            }

            if (cursorComp?.token !== undefined) {
                const tokenBox = tokenLookup[cursorComp.token];
                if (!!tokenBox) {
                    commands.push(context => drawCursorToken(context, cursorComp, tokenBox));
                }
            }
        }

        return commands;
    }
}

function drawBoxes(context: CanvasRenderingContext2D, boxes: Box[]): void {
    for (const box of boxes) {
        CanvasHelperStatics.fillRectangle(
            context,
            box,
            "#ffb2b2",
            true);
    }
}

function drawCursorToken(context: CanvasRenderingContext2D, cursorComp: CursorComponent, tokenBox: Box): void {
    const text = String(cursorComp.token);
    const textMetrics = context.measureText(text);

    const margin = 4;
    const halfMargin = margin / 2;

    const boxWidth = textMetrics.width + margin;
    const boxHeight = textMetrics.actualBoundingBoxAscent + margin;
    const boxPos: Point = {
        x: boxCenterX(tokenBox) - boxWidth / 2,
        y: tokenBox.top - boxHeight - halfMargin
    }

    context.globalCompositeOperation = 'source-over'
    context.fillStyle = 'white';
    context.fillRect(boxPos.x, boxPos.y, boxWidth, boxHeight);
    context.fillStyle = 'black';
    context.fillText(String(cursorComp.token), boxPos.x + halfMargin, boxPos.y + boxHeight - halfMargin);

    context.globalCompositeOperation = 'multiply'
    CanvasHelperStatics.fillRectangle(
        context,
        tokenBox,
        "green",
        true);
}
